import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defer, map, Observable, of, switchMap, take, tap } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { FeatureFlagService } from '../../global-store/feature-flag.service';

import {
  ContractSyncProgressModel,
  PoolConnectionModel,
  PoolConnectionStatusModel,
  UpdatePoolConnectionInformationRequestModel,
  UpdatePoolConnectionSettingsRequestModel,
} from '../../model';
import RequestCache from '../../utils/RequestCache';

@Injectable({
  providedIn: 'root',
})
export class AccountPoolService {
  public readonly poolConnectionInformation: RequestCache<PoolConnectionModel | null>;

  constructor(
    private http: HttpClient,
    featureFlagService: FeatureFlagService,
  ) {
    this.poolConnectionInformation = new RequestCache(
      featureFlagService.isFeatureEnabled('POOL').pipe(
        take(1),
        switchMap((flag) => {
          if (!flag) {
            return of(null);
          }

          return this.http
            .get<PoolConnectionModel>(`${environment.apiEndpoint}/account/pool/connection`, {
              observe: 'response',
            })
            .pipe(map((response) => (response.status === 204 ? null : response.body!)));
        }),
      ),
    );
  }

  public connection(): Observable<PoolConnectionModel> {
    return this.http
      .get<PoolConnectionModel>(`${environment.apiEndpoint}/account/pool/connection`, {
        observe: 'response',
      })
      .pipe(map((response) => (response.status === 204 ? null : response.body!)));
  }

  public getStatusOfRunningPool(): Observable<PoolConnectionStatusModel[]> {
    return this.http.get<PoolConnectionStatusModel[]>(`${environment.apiEndpoint}/account/pool/sync?limit=1`);
  }

  public getSyncJob(jobId: string): Observable<PoolConnectionStatusModel> {
    return this.http.get<PoolConnectionStatusModel>(`${environment.apiEndpoint}/account/pool/sync/${jobId}`);
  }

  /** Contract sync snapshot (incl. per-contract report) of the b2bapi child job for an account sync job. */
  public getContractSyncReport(parentJobId: string): Observable<ContractSyncProgressModel> {
    return this.http.get<ContractSyncProgressModel>(`${environment.apiEndpoint}/b2b/contract/sync`, {
      params: { parentJobId },
    });
  }

  public authToken(): Observable<string> {
    return this.http
      .get(`${environment.apiEndpoint}/account/pool/access-token`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(map((response) => (response.status === 204 ? null : response.body!)));
  }

  public updatePoolSettings(body: UpdatePoolConnectionSettingsRequestModel): Observable<void> {
    return this.http
      .put<void>(`${environment.apiEndpoint}/account/pool/settings`, body)
      .pipe(tap(() => this.poolConnectionInformation.clearCache()));
  }

  public synchronizeClients(direction: 'VINLIVT_TO_POOL' | 'POOL_TO_VINLIVT'): Observable<void> {
    return defer(() => {
      this.poolConnectionInformation.updateValue((current) => ({ ...current, locked: true }));
      return this.http.post<void>(`${environment.apiEndpoint}/account/pool/synchronize/${direction}`, null).pipe(
        tap((): void =>
          this.poolConnectionInformation.updateValue((current: PoolConnectionModel) => ({
            ...current,
            locked: false,
          })),
        ),
      );
    });
  }

  public updatePoolConnectionInformation(
    body: UpdatePoolConnectionInformationRequestModel,
  ): Observable<PoolConnectionModel> {
    // This endpoint will create a new connection if no connection exists yet.
    //  Restrictions
    //    The pool connection cannot be updated while a synchronization is in progress.
    //    The connection cannot be changed to a different pool type.
    //    On netfonds, the username cannot be changed.

    return this.http
      .put<PoolConnectionModel>(`${environment.apiEndpoint}/account/pool/connection`, body)
      .pipe(tap(() => this.poolConnectionInformation.clearCache()));
  }

  public createBeraterWeltAuthorizationUrl(): Observable<string> {
    return this.http.get(`${environment.apiEndpoint}/account/pool/auth/beraterwelt/authorization-url`, {
      responseType: 'text',
    });
  }

  public unlinkPoolConnection(): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/account/pool/connection`);
  }

  public unlockSynchronisation(): Observable<void> {
    return this.http.post<void>(`${environment.apiEndpoint}/account/pool/connection/unlock`, null);
  }

  public synchronizeSpecificClient(
    //   Preconditions
    //      There are several reasons why this synchronization might not be successful. In that case the endpoint will return a 412 status code, and a error code will be provided in the response body.
    //
    //    CLIENT_NOT_LINKED
    //      Direction: Pool to Vinlivt
    //      Condition: The Vinlivt client is not linked to a pool client.
    //      Reason: The client must be linked to the pool client in order to synchronize.
    //    POOL_CREATION_DISABLED
    //      Direction: Vinlivt to Pool
    //      Condition:
    //        The Vinlivt client is not linked to a pool client.
    //        Creation of pool clients is disabled.
    //      Reason: The pool does not allow creation of new clients, so the synchronization cannot create a new client in the pool.
    //    POOL_RECREATE_DISABLED
    //      Direction: Vinlivt to Pool
    //      Condition:
    //        The Vinlivt client is linked to a pool client.
    //        The pool client referenced does not exist in the pool.
    //        Re-creation of pool clients is disabled.
    //      Reason: The pool does not allow re-creation of existing clients, so the synchronization cannot update the existing client in the pool.

    direction: 'VINLIVT_TO_POOL' | 'POOL_TO_VINLIVT',
    clientId: string,
  ): Observable<void> {
    return defer(() => {
      this.poolConnectionInformation.updateValue((current) => ({ ...current, locked: true }));
      return this.http
        .post<void>(`${environment.apiEndpoint}/account/pool/synchronize/${direction}/${clientId}`, null)
        .pipe(tap(() => this.poolConnectionInformation.updateValue((current) => ({ ...current, locked: false }))));
    });
  }
}
